<template>
<div class="container py-3">

  <!-- POS Receipt Preview Modal Overlay -->
  <div v-if="showReceiptPreview" class="receipt-preview-overlay d-flex align-items-center justify-content-center">
    <div class="card shadow-lg border-0" style="width: 360px; max-width: 95vw;">
      <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
        <h6 class="mb-0 font-weight-bold">Receipt Preview</h6>
        <button type="button" class="close text-white" @click="closeReceiptPreview">&times;</button>
      </div>
      
      <div class="card-body p-3 bg-secondary">
        <!-- Printable POS Receipt Container (80mm format) -->
        <div id="pos-receipt">
          <div style="width: 72mm; max-width: 100%; margin: 0 auto; padding: 12px; font-family: 'Courier New', Courier, monospace; font-size: 12px; color: #000; line-height: 1.3; box-sizing: border-box; background: #fff;">
            <!-- Line 1: 'Receipt' as title (centrally aligned) -->
            <div style="text-align: center; font-weight: bold; font-size: 16px; text-transform: uppercase; margin-bottom: 4px;">Receipt</div>
            
            <!-- Line 2: invoiceDate -->
            <div style="text-align: center; font-size: 11px; margin-bottom: 4px; color: #333;">{{ formattedInvoiceDate }}</div>
            
            <!-- Line 3: Org Name (say Ketco) -->
            <div style="text-align: center; font-weight: bold; font-size: 14px; margin-bottom: 8px;">{{ orgName || 'Ketco' }}</div>

            <!-- List of items in order: item1name + Item2Name, Price, Qty, Price -->
            <div style="border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 6px 0; margin: 8px 0; font-size: 12px;">
              <div v-for="(item, idx) in selectedOrderItems" :key="idx" style="margin-bottom: 8px;">
                <div style="font-weight: bold; word-break: break-word;">{{ getItemDisplayName(item) }}</div>
                <table style="width: 100%; border: none; font-family: inherit; font-size: 11px; margin-top: 2px;">
                  <tr>
                    <td style="text-align: left; color: #555; padding: 0; border: none;">
                      {{ Number((getItemSubtotal(item) / (item.qty || item.quantity || 1))).toFixed(2) }} &times; {{ item.qty || item.quantity || 1 }}
                    </td>
                    <td style="text-align: right; font-weight: bold; color: #000; padding: 0; border: none;">
                      {{ getItemSubtotal(item) }}
                    </td>
                  </tr>
                </table>
              </div>
            </div>

            <!-- Finally show total for the order -->
            <table style="width: 100%; border: none; font-family: inherit; font-size: 14px; font-weight: bold; margin: 8px 0;">
              <tr>
                <td style="text-align: left; padding: 0; border: none;">TOTAL:</td>
                <td style="text-align: right; padding: 0; border: none;">INR {{ selectedOrderTotal }}</td>
              </tr>
            </table>

            <!-- Note (if present) -->
            <div v-if="selectedOrderNote" style="border-top: 1px dashed #000; padding-top: 6px; margin-top: 6px; font-size: 11px; text-align: left; word-break: break-word;">
              <strong>Note:</strong> {{ selectedOrderNote }}
            </div>

            <!-- Footer: Thank you for your business. -->
            <div style="border-top: 1px dashed #000; padding-top: 8px; margin-top: 10px; text-align: center;">
              <div style="font-weight: bold; font-size: 12px; margin-bottom: 4px;">Thank you for your business.</div>
              <!-- Bottom: order['orderNbr'] -->
              <div style="font-size: 11px; font-weight: bold; color: #444;">{{ selectedOrderNbr }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Options: ESC/POS Print, Web Print and Cancel -->
      <div class="card-footer bg-white py-2">
        <div v-if="printInProgress" class="alert alert-info py-1 px-2 mb-2 text-center font-weight-bold" style="font-size: 13px;">
          <i class="fa fa-spinner fa-spin mr-1"></i> Receipt Print in progress. Please wait...
        </div>
        <div class="d-flex justify-content-between align-items-center">
          <button type="button" class="btn btn-secondary btn-sm" @click="closeReceiptPreview" :disabled="printInProgress">
            Cancel
          </button>
          <button type="button" class="btn btn-dark btn-sm font-weight-bold" @click="printESCPOSDirectOrder('auto')" :disabled="printInProgress" title="Send raw ESC/POS commands to Thermal Printer">
            <i class="fa" :class="printInProgress ? 'fa-spinner fa-spin' : 'fa-print'"></i> Print
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Tax Invoice Preview Modal Overlay -->
  <div v-if="showInvoicePreview" class="receipt-preview-overlay d-flex align-items-center justify-content-center">
    <div class="card shadow-lg border-0" style="width: 750px; max-width: 95vw; max-height: 95vh; overflow-y: auto;">
      <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center py-2">
        <h6 class="mb-0 font-weight-bold">Tax Invoice Preview</h6>
        <button type="button" class="close text-white" @click="closeInvoicePreview">&times;</button>
      </div>
      
      <div class="card-body p-4 bg-light">
        <!-- Printable Tax Invoice Container -->
        <div id="tax-invoice" style="width: 100%; max-width: 200mm; margin: 0 auto; padding: 15px; font-family: 'Times New Roman', Times, serif; color: #000; background: #fff; box-sizing: border-box; border: 2px solid #000;">
          
          <!-- Top Header Info -->
          <table style="width: 100%; border-collapse: collapse; border: none; font-family: 'Times New Roman', Times, serif; font-size: 12px; font-weight: bold; text-transform: uppercase;">
            <tr>
              <td style="width: 33%; text-align: left; vertical-align: top; border: none; padding: 0;">
                <div>GST No: 27AABFK2206K1ZK</div>
                <div>PAN No: AABFK 2206K</div>
              </td>
              <td style="width: 34%; text-align: center; vertical-align: top; border: none; padding: 0;">
                <u style="font-size: 16px; font-weight: bold; display: block;">TAX INVOICE</u>
                <u style="font-size: 13px; font-weight: bold; display: block;">CASH MEMO</u>
              </td>
              <td style="width: 33%; text-align: right; vertical-align: top; border: none; padding: 0;">
                <div style="font-size: 12px; font-weight: bold; margin-top: 4px;">Invoice No: {{ invoiceNbrDisplay }}</div>
              </td>
            </tr>
          </table>

          <!-- Company Branding Header -->
          <div style="text-align: center; margin-top: 8px; margin-bottom: 8px; font-family: 'Times New Roman', Times, serif;">
            <div style="font-size: 24px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; color: #000;">
              KRISHNA ELECTRIC & TRADING CO.
            </div>
            <div style="font-size: 13px; font-weight: bold; margin-top: 2px;">
              <u>Show Room / Office</u> : Shop No.12, Municipal Bldg., Nana Chowk, MUMBAI - 400 007.
            </div>
            <div style="font-size: 13px; font-weight: bold; margin-top: 3px;">
              <u>Manufacturers of :</u>
            </div>
            <div style="font-size: 13px; font-weight: bold; letter-spacing: 0.5px;">
              <span style="font-size: 15px; font-weight: bold;">KETCO<sup>&reg;</sup></span> BRASS BASE / LAUNDRY & TAILOR IRONS
            </div>
            <div style="font-size: 13px; font-weight: bold;">
              MICA ELEMENTS, SOCKETS
            </div>
            <div style="font-family: 'Georgia', serif; font-style: italic; font-size: 24px; font-weight: bold; margin-top: 4px; margin-bottom: 6px;">
              KETCO<sup>&reg;</sup>
            </div>
          </div>

          <!-- Customer & Date Header -->
          <table style="width: 100%; border-collapse: collapse; border-top: 1px solid #000; border-bottom: 1px solid #000; margin: 8px 0; font-family: 'Times New Roman', Times, serif; font-size: 13px; font-weight: bold;">
            <tr>
              <td style="text-align: left; padding: 6px 0; border: none;">
                M/s. <span style="border-bottom: 1px solid #000; padding: 0 10px;">{{ invoiceCustomerName }}</span>
              </td>
              <td style="text-align: right; padding: 6px 0; border: none;">
                Date <span style="border-bottom: 1px solid #000; padding: 0 10px;">{{ formattedInvoiceDateForMemo }}</span>
              </td>
            </tr>
          </table>

          <!-- Invoice Item Table -->
          <table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-collapse: collapse; border: 1px solid #000; font-family: 'Times New Roman', Times, serif; font-size: 13px;">
            <thead>
              <tr style="background-color: #f2f2f2; text-align: center; font-weight: bold;">
                <th style="width: 8%; border: 1px solid #000; text-align: center; padding: 6px 4px;">Qty.</th>
                <th style="width: 52%; border: 1px solid #000; text-align: center; padding: 6px 4px;">Description</th>
                <th style="width: 15%; border: 1px solid #000; text-align: center; padding: 6px 4px;">Rate</th>
                <th style="width: 15%; border: 1px solid #000; text-align: center; padding: 6px 4px;">Rs.</th>
                <th style="width: 10%; border: 1px solid #000; text-align: center; padding: 6px 4px;">P.</th>
              </tr>
            </thead>
            <tbody>
              <tr style="font-weight: bold;">
                <td style="border: 1px solid #000;"></td>
                <td colspan="4" style="border: 1px solid #000; text-align: left; padding: 5px 8px;">HSN CODE : 8516</td>
              </tr>
              <tr v-for="(item, idx) in invoiceItems" :key="idx">
                <td style="border: 1px solid #000; text-align: center; font-weight: bold; padding: 5px 4px;">{{ item.qty || item.quantity || 1 }}</td>
                <td style="border: 1px solid #000; text-align: left; padding: 5px 8px;">{{ getItemDisplayName(item) }}</td>
                <td style="border: 1px solid #000; text-align: right; padding: 5px 8px;">{{ formatAmount(item.price) }}</td>
                <td style="border: 1px solid #000; text-align: right; font-weight: bold; padding: 5px 8px;">{{ getRupees(getItemLineTotal(item)) }}</td>
                <td style="border: 1px solid #000; text-align: center; padding: 5px 4px;">{{ getPaise(getItemLineTotal(item)) }}</td>
              </tr>
              <!-- Empty rows filler for paper memo aspect ratio -->
              <tr v-for="n in invoiceEmptyRowsCount" :key="'empty-'+n" style="height: 28px;">
                <td style="border: 1px solid #000;"></td>
                <td style="border: 1px solid #000;"></td>
                <td style="border: 1px solid #000;"></td>
                <td style="border: 1px solid #000;"></td>
                <td style="border: 1px solid #000;"></td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2" rowspan="5" style="border: 1px solid #000; vertical-align: bottom; padding: 8px; text-align: left;">
                  <div style="font-weight: bold; margin-bottom: 4px;">Goods once sold will not be taken back</div>
                  <div>Guarantee for__________________year/month.</div>
                  <div>Guarantee for Element Only.</div>
                </td>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">Total</td>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">{{ getRupees(invoiceBaseTotal) }}</td>
                <td style="border: 1px solid #000; font-weight: bold; text-align: center; padding: 5px 4px;">{{ getPaise(invoiceBaseTotal) }}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">CGST {{ invoiceCGSTPercent }}</td>
                <td style="border: 1px solid #000; text-align: right; padding: 5px 8px;">{{ getRupees(invoiceCGSTAmt) }}</td>
                <td style="border: 1px solid #000; text-align: center; padding: 5px 4px;">{{ getPaise(invoiceCGSTAmt) }}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">SGST {{ invoiceSGSTPercent }}</td>
                <td style="border: 1px solid #000; text-align: right; padding: 5px 8px;">{{ getRupees(invoiceSGSTAmt) }}</td>
                <td style="border: 1px solid #000; text-align: center; padding: 5px 4px;">{{ getPaise(invoiceSGSTAmt) }}</td>
              </tr>
              <tr>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">IGST {{ invoiceIGSTPercent }}</td>
                <td style="border: 1px solid #000; text-align: right; padding: 5px 8px;">{{ getRupees(invoiceIGSTAmt) }}</td>
                <td style="border: 1px solid #000; text-align: center; padding: 5px 4px;">{{ getPaise(invoiceIGSTAmt) }}</td>
              </tr>
              <tr style="font-weight: bold; background-color: #f2f2f2;">
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">G. Total</td>
                <td style="border: 1px solid #000; font-weight: bold; text-align: right; padding: 5px 8px;">{{ getRupees(invoiceGrandTotal) }}</td>
                <td style="border: 1px solid #000; border-collapse: collapse; font-weight: bold; text-align: center; padding: 5px 4px;">{{ getPaise(invoiceGrandTotal) }}</td>
              </tr>
            </tfoot>
          </table>

          <!-- Footer Signature -->
          <table style="width: 100%; margin-top: 25px; border: none; font-family: 'Times New Roman', Times, serif; font-size: 13px; font-weight: bold;">
            <tr>
              <td style="border: none; text-align: left; vertical-align: bottom; width: 50%;">
                Customer's GSTN: <span>{{ invoiceCustomerGSTN }}</span>
              </td>
              <td style="border: none; text-align: right; vertical-align: bottom; width: 50%;">
                Krishna Electric & Trading Co.
                <div>Phone : +91 93245 50216</div>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <!-- 2 Options: Print and Cancel -->
      <div class="card-footer bg-white d-flex justify-content-between py-2">
        <button type="button" class="btn btn-secondary btn-sm" @click="closeInvoicePreview">
          Cancel
        </button>
        <button type="button" class="btn btn-success btn-sm font-weight-bold" @click="printTaxInvoice">
          <i class="fa fa-print mr-1"></i> Print Tax Invoice
        </button>
      </div>
    </div>
  </div>

  <div class="d-flex justify-content-between align-items-center mb-4">
    <div>
      <h3 class="font-weight-bold mb-1">Order Report</h3>
      <p class="text-muted small mb-0">View sales transactions by date</p>
    </div>
    <div>
      <button class="btn btn-outline-primary btn-sm" @click="fetchOrders" :disabled="loading">
        <i class="fa fa-refresh mr-1"></i> Refresh Report
      </button>
    </div>
  </div>

  <!-- Date Selection Bar -->
  <div class="card mb-4 shadow-sm">
    <div class="card-body p-3">
      <div class="form-group row align-items-center mb-0">
        <label class="col-sm-2 col-form-label font-weight-bold text-sm-right">Select Date:</label>
        <div class="col-sm-4">
          <datepicker 
            v-model="selectedDate" 
            @selected="onDateSelected" 
            input-class="form-control form-control-sm bg-white"
            :bootstrap-styling="true">
          </datepicker>
        </div>
        <div class="col-sm-3 mt-2 mt-sm-0">
          <button class="btn btn-primary btn-sm px-4" @click="fetchOrders" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
            Fetch Orders
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="row mb-4">
    <div class="col-md-3 mb-3 mb-md-0">
      <div class="card bg-light border-0 shadow-sm">
        <div class="card-body p-3">
          <div class="text-uppercase text-muted font-weight-bold small mb-1">Total Orders</div>
          <div class="h4 font-weight-bold mb-0 text-primary">{{ orders.length }}</div>
        </div>
      </div>
    </div>
    <div class="col-md-3 mb-3 mb-md-0">
      <div class="card bg-light border-0 shadow-sm">
        <div class="card-body p-3">
          <div class="text-uppercase text-muted font-weight-bold small mb-1">Total Amount</div>
          <div class="h4 font-weight-bold mb-0 text-success">INR {{ totalRevenue }}</div>
        </div>
      </div>
    </div>
    <div class="col-md-3 mb-3 mb-md-0">
      <div class="card bg-light border-0 shadow-sm">
        <div class="card-body p-3">
          <div class="text-uppercase text-muted font-weight-bold small mb-1">Total Cash</div>
          <div class="h4 font-weight-bold mb-0 text-info">INR {{ totalPaidCash }}</div>
        </div>
      </div>
    </div>
    <div class="col-md-3">
      <div class="card bg-light border-0 shadow-sm">
        <div class="card-body p-3">
          <div class="text-uppercase text-muted font-weight-bold small mb-1">Total Bank</div>
          <div class="h4 font-weight-bold mb-0 text-dark">INR {{ totalPaidBank }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Orders Table -->
  <div class="card shadow-sm">
    <div class="card-body p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="thead-light">
            <tr>
              <th>Order Number</th>
              <th>Create Time</th>
              <th>Sale Date</th>
              <th class="text-right">Total</th>
              <th class="text-right">Paid Cash</th>
              <th class="text-right">Paid Bank</th>
              <th class="text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="7" class="text-center py-4 text-muted">
                Loading order report...
              </td>
            </tr>
            <tr v-else-if="orders.length === 0">
              <td colspan="7" class="text-center py-4 text-muted">
                No orders found for selected date.
              </td>
            </tr>
            <tr v-else v-for="(order, index) in orders" :key="index">
              <td class="font-weight-bold small">{{ getOrderShortNbr(order) }}</td>
              <td class="small">{{ formatCreateTime(order) }}</td>
              <td class="small">{{ formatInvoiceDate(order) }}</td>
              <td class="text-right font-weight-bold small">{{ formatAmount(order.total) }}</td>
              <td class="text-right small">{{ formatAmount(order.paidCash) }}</td>
              <td class="text-right small">{{ formatAmount(order.paidBank) }}</td>
              <td class="text-center">
                <button class="btn btn-sm btn-outline-primary py-0 mr-1" @click="openReceiptPreview(order)" title="Print Receipt">
                  <i class="fa fa-print mr-1"></i> Receipt
                </button>
                <button v-if="order.invNbr && order.invNbr.trim() !== ''" class="btn btn-sm btn-outline-success py-0" @click="openInvoicePreview(order)" title="Print Tax Invoice">
                  <i class="fa fa-file-text-o mr-1"></i> Invoice
                </button>
                <button v-else class="btn btn-sm btn-outline-warning py-0" @click="fetchInvoiceNumber(order)" :disabled="order.loadingInv" title="Get Invoice Number">
                  <span v-if="order.loadingInv" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
                  <i v-else class="fa fa-file-text-o mr-1"></i> Get Inv No
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="orders.length > 0">
            <tr class="font-weight-bold bg-light">
              <td colspan="3" class="text-right">Total:</td>
              <td class="text-right">{{ totalRevenue }}</td>
              <td class="text-right">{{ totalPaidCash }}</td>
              <td class="text-right">{{ totalPaidBank }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import axios from 'axios';
import { printESCPOSDirect } from '../utils/escpos';

export default {
  name: 'OrderReport',
  components: {
    Datepicker
  },
  data() {
    return {
      selectedDate: new Date(),
      orders: [],
      loading: false,
      showReceiptPreview: false,
      selectedOrderForReceipt: null,
      showInvoicePreview: false,
      selectedOrderForInvoice: null,
      printInProgress: false,
      printStyles: `<style>
        #tax-invoice {
          width: 100% !important;
          max-width: 200mm !important;
          margin: 0 auto !important;
          padding: 15px !important;
          font-family: 'Times New Roman', Times, serif !important;
          color: #000 !important;
          background-color: #fff !important;
          box-sizing: border-box !important;
          border: 1px solid #000 !important;
        }
        #tax-invoice *, #tax-invoice *::before, #tax-invoice *::after {
          box-sizing: border-box !important;
        }
        #tax-invoice .ti-header {
          display: flex !important;
          justify-content: space-between !important;
          align-items: flex-start !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          margin-bottom: 5px !important;
        }
        #tax-invoice .ti-company-header {
          text-align: center !important;
          margin-top: 5px !important;
          margin-bottom: 10px !important;
        }
        #tax-invoice .ti-company-name {
          font-family: 'Times New Roman', Times, serif !important;
          font-size: 22px !important;
          font-weight: bold !important;
          text-transform: uppercase !important;
          letter-spacing: 1px !important;
          color: #000 !important;
          margin: 0 !important;
        }
        #tax-invoice .ti-subtext {
          font-size: 13px !important;
          font-weight: bold !important;
          color: #000 !important;
          margin-top: 2px !important;
        }
        #tax-invoice .ti-brand-title {
          font-family: 'Georgia', serif !important;
          font-style: italic !important;
          font-size: 24px !important;
          font-weight: bold !important;
          color: #000 !important;
          margin-top: 4px !important;
          margin-bottom: 8px !important;
        }
        #tax-invoice .ti-meta-bar {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          border-top: 1px solid #000 !important;
          border-bottom: 1px solid #000 !important;
          padding: 6px 0 !important;
          margin: 8px 0 !important;
          font-size: 13px !important;
          font-weight: bold !important;
        }
        #tax-invoice table.ti-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 8px !important;
          margin-bottom: 8px !important;
          font-family: 'Times New Roman', Times, serif !important;
          font-size: 13px !important;
        }
        #tax-invoice table.ti-table th, 
        #tax-invoice table.ti-table td {
          border: 1px solid #000 !important;
          padding: 5px 8px !important;
          color: #000 !important;
          vertical-align: middle !important;
        }
        #tax-invoice table.ti-table th {
          background-color: #f2f2f2 !important;
          font-weight: bold !important;
          text-align: center !important;
        }
        #tax-invoice .text-center { text-align: center !important; }
        #tax-invoice .text-right { text-align: right !important; }
        #tax-invoice .text-left { text-align: left !important; }
        #tax-invoice .font-weight-bold { font-weight: bold !important; }
        #tax-invoice .ti-footer-sign {
          display: flex !important;
          justify-content: flex-end !important;
          margin-top: 25px !important;
          font-size: 13px !important;
          font-weight: bold !important;
        }
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          #tax-invoice {
            width: 100% !important;
            max-width: 100% !important;
            border: 1px solid #000 !important;
            padding: 15mm !important;
            margin: 0 !important;
            box-sizing: border-box !important;
          }
        }
      </style>`
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'currentUser', 'orgId', 'location', 'orgName']),
    totalRevenue() {
      const sum = this.orders.reduce((acc, order) => acc + Number(order.total || 0), 0);
      return sum.toFixed(2);
    },
    totalPaidCash() {
      const sum = this.orders.reduce((acc, order) => acc + Number(order.paidCash || 0), 0);
      return sum.toFixed(2);
    },
    totalPaidBank() {
      const sum = this.orders.reduce((acc, order) => acc + Number(order.paidBank || 0), 0);
      return sum.toFixed(2);
    },
    formattedInvoiceDate() {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const day = pad(now.getDate());
      const month = pad(now.getMonth() + 1);
      const year = String(now.getFullYear()).slice(-2);
      const hours = pad(now.getHours());
      const minutes = pad(now.getMinutes());
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    },
    selectedOrderItems() {
      if (this.selectedOrderForReceipt && this.selectedOrderForReceipt.items) {
        return this.selectedOrderForReceipt.items;
      }
      return [];
    },
    selectedOrderTotal() {
      if (this.selectedOrderForReceipt && this.selectedOrderForReceipt.total !== undefined) {
        return Number(this.selectedOrderForReceipt.total).toFixed(2);
      }
      return '0.00';
    },
    selectedOrderNbr() {
      if (this.selectedOrderForReceipt) {
        return this.selectedOrderForReceipt.orderNbr || this.selectedOrderForReceipt.orderNbrShort || this.selectedOrderForReceipt.orderShortNbr || 'N/A';
      }
      return 'N/A';
    },
    selectedOrderNote() {
      if (this.selectedOrderForReceipt) {
        return String(this.selectedOrderForReceipt.note || this.selectedOrderForReceipt.paymentNote || this.selectedOrderForReceipt.notes || '').trim();
      }
      return '';
    },

    // Invoice Computeds
    invoiceNbrDisplay() {
      if (this.selectedOrderForInvoice) {
        return this.selectedOrderForInvoice.invNbr || '';
      }
      return '';
    },
    invoiceCustomerName() {
      if (this.selectedOrderForInvoice) {
        if (this.selectedOrderForInvoice.partner && this.selectedOrderForInvoice.partner.name) {
          return this.selectedOrderForInvoice.partner.name;
        }
        if (this.selectedOrderForInvoice.customerEmail) {
          return this.selectedOrderForInvoice.customerEmail;
        }
      }
      return '___________________________';
    },
    invoiceCustomerGSTN() {
      if (this.selectedOrderForInvoice) {
        if (this.selectedOrderForInvoice.GSTN) return this.selectedOrderForInvoice.GSTN;
        if (this.selectedOrderForInvoice.partner && this.selectedOrderForInvoice.partner.GSTN) {
          return this.selectedOrderForInvoice.partner.GSTN;
        }
      }
      return '____________________';
    },
    formattedInvoiceDateForMemo() {
      let d = new Date();
      let day = String(d.getDate()).padStart(2, '0');
      let month = String(d.getMonth() + 1).padStart(2, '0');
      let year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },
    invoiceItems() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.items) {
        return this.selectedOrderForInvoice.items;
      }
      return [];
    },
    invoiceEmptyRowsCount() {
      const minRows = 10;
      const count = this.invoiceItems.length;
      return count < minRows ? minRows - count : 0;
    },
    invoiceBaseTotal() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.items && this.selectedOrderForInvoice.items.length > 0) {
        return this.selectedOrderForInvoice.items.reduce((sum, item) => {
          return sum + this.getItemLineTotal(item);
        }, 0);
      }
      if (this.selectedOrderForInvoice) {
        if (this.selectedOrderForInvoice.baseTotal !== undefined && this.selectedOrderForInvoice.baseTotal > 0) {
          return this.selectedOrderForInvoice.baseTotal;
        }
        const total = Number(this.selectedOrderForInvoice.total || 0);
        const tax = Number(this.selectedOrderForInvoice.tax || 0);
        return total - tax;
      }
      return 0;
    },
    invoiceTaxTotal() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.tax !== undefined) {
        return Number(this.selectedOrderForInvoice.tax || 0);
      }
      return 0;
    },
    invoiceCGSTPercent() {
      return '%';
    },
    invoiceCGSTAmt() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.items) {
        let sum = 0;
        this.selectedOrderForInvoice.items.forEach(i => {
          if (i.CGST && i.CGST.amt) sum += Number(i.CGST.amt);
        });
        if (sum > 0) return sum;
      }
      return this.invoiceTaxTotal > 0 ? (this.invoiceTaxTotal / 2) : 0;
    },
    invoiceSGSTPercent() {
      return '%';
    },
    invoiceSGSTAmt() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.items) {
        let sum = 0;
        this.selectedOrderForInvoice.items.forEach(i => {
          if (i.SGST && i.SGST.amt) sum += Number(i.SGST.amt);
        });
        if (sum > 0) return sum;
      }
      return this.invoiceTaxTotal > 0 ? (this.invoiceTaxTotal / 2) : 0;
    },
    invoiceIGSTPercent() {
      return '%';
    },
    invoiceIGSTAmt() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.items) {
        let sum = 0;
        this.selectedOrderForInvoice.items.forEach(i => {
          if (i.IGST && i.IGST.amt) sum += Number(i.IGST.amt);
        });
        if (sum > 0) return sum;
      }
      return 0;
    },
    invoiceGrandTotal() {
      if (this.selectedOrderForInvoice && this.selectedOrderForInvoice.total !== undefined) {
        return Number(this.selectedOrderForInvoice.total || 0);
      }
      return 0;
    }
  },
  watch: {
    isLoggedIn(newVal) {
      if (newVal) {
        this.fetchOrders();
      }
    }
  },
  mounted() {
    if (this.isLoggedIn) {
      this.fetchOrders();
    }
  },
  methods: {
    ...mapActions(['addMessage']),

    getMidDay(dt) {
      if (typeof(dt) == 'string')
        dt = new Date(dt)
      dt.setHours(12)
      dt.setMinutes(0)
      dt.setSeconds(0)
      dt.setMilliseconds(0)
      return dt
    },

    onDateSelected(date) {
      this.selectedDate = date;
      this.$nextTick(() => {
        this.fetchOrders();
      });
    },

    fetchOrders() {
      let dateVal = this.selectedDate ? new Date(this.selectedDate) : new Date();
      let nextDateVal = new Date(dateVal);
      nextDateVal.setDate(nextDateVal.getDate() + 1);

      let payload = {
        'opType': 'Entry',
        'location': this.location,
        'orderType': 'Sale',
        'dateRangeType': 'invoiceDate',
        'fromDate': this.getMidDay(dateVal),
        'toDate': this.getMidDay(nextDateVal),
        'invoiceIssued': '',
        'paymentType': '',
        'orderNbrShort': '',
        'altNbr': ''
      };
      this.serverCall('GetOrders', payload);
    },

    serverCall(name, params) {
      var self = this;
      if (this.isLoggedIn) {
        if (this.currentUser && typeof this.currentUser.getIdToken === 'function') {
          this.currentUser.getIdToken().then(function(idToken) {
            switch (name) {
              case 'GetOrders':
                self.getOrders(idToken, params);
                break;
              case 'GetInvNbr':
                self.getInvNbr(idToken, params);
                break;
              default:
                console.log('Unknown function: ' + name);
            }
          }).catch(function(error) {
            self.addMessage({ messageClass: "danger", message: String(error) });
          });
        }
      } else {
        this.addMessage({ messageClass: "warning", message: "User logged out. Please login." });
      }
    },

    getOrders(idToken, params) {
      var self = this;
      self.loading = true;
      axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_ROOT_API,
        url: '/GetOrders/',
        data: params,
        headers: {
          'Authorization': 'Bearer ' + idToken,
          'OrgId': self.orgId,
          'Content-Type': 'application/json',
        },
      })
      .then(function (response) {
        self.loading = false;
        if (response.data && response.data.err) {
          self.addMessage({ messageClass: "danger", message: response.data.err });
        } else {
          var data = response.data;
          var list = (data && Array.isArray(data.dataList)) ? data.dataList : [];
          list = list.filter(function (order) {
            return order.cancelled !== 'Yes' && String(order.cancelled).toLowerCase() !== 'yes';
          });
          list.sort(function (a, b) {
            var timeA = a.create_time_sl || '';
            var timeB = b.create_time_sl || '';
            return timeA.localeCompare(timeB);
          });
          self.orders = list;
        }
      })
      .catch(function (error) {
        self.loading = false;
        self.addMessage({ messageClass: "danger", message: String(error) });
      });
    },

    fetchInvoiceNumber(order) {
      this.serverCall('GetInvNbr', order);
    },

    getInvNbr(idToken, order) {
      var self = this;
      this.$set(order, 'loadingInv', true);
      axios({
        method: 'POST',
        baseURL: process.env.VUE_APP_ROOT_API,
        url: '/GetInvNbr/',
        data: order,
        headers: {
          'Authorization': 'Bearer ' + idToken,
          'OrgId': self.orgId,
          'Content-Type': 'application/json',
        },
      })
      .then(function (response) {
        self.$set(order, 'loadingInv', false);
        if (response.data && response.data.err && response.data.err !== '') {
          self.addMessage({ messageClass: "danger", message: response.data.err });
        } else if (response.data && response.data.invNbr) {
          self.$set(order, 'invNbr', response.data.invNbr);
          self.addMessage({ messageClass: "success", message: "Invoice number generated: " + response.data.invNbr });
        } else {
          self.addMessage({ messageClass: "danger", message: "Failed to retrieve invoice number." });
        }
      })
      .catch(function (error) {
        self.$set(order, 'loadingInv', false);
        self.addMessage({ messageClass: "danger", message: String(error) });
      });
    },

    getOrderShortNbr(order) {
      if (!order) return '-';
      return order.orderNbrShort || order.orderShortNbr || order.orderNbr || '-';
    },

    formatCreateTime(order) {
      if (!order) return '-';
      const timeVal = order.create_time_sl || order.create_time || '';

      const pad = (n) => String(n).padStart(2, '0');
      const toDDMMYY = (d) => {
        const day = pad(d.getDate());
        const month = pad(d.getMonth() + 1);
        const year = String(d.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
      };
      const toHHMM = (d) => {
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        return `${hours}:${minutes}`;
      };

      // 1. If timeVal itself is a Date or timestamp object
      if (typeof timeVal === 'object' && timeVal !== null) {
        const sec = timeVal._seconds || timeVal.seconds;
        if (sec) {
          const d = new Date(sec * 1000);
          return `${toDDMMYY(d)} ${toHHMM(d)}`;
        }
        if (timeVal instanceof Date && !isNaN(timeVal.getTime())) {
          return `${toDDMMYY(timeVal)} ${toHHMM(timeVal)}`;
        }
      }

      const str = String(timeVal).trim();

      // 2. If timeVal already contains full date + time
      if (str.includes('T') || (str.includes('-') && str.includes(':')) || (str.includes('/') && str.includes(':'))) {
        const parsed = new Date(str);
        if (!isNaN(parsed.getTime())) {
          return `${toDDMMYY(parsed)} ${toHHMM(parsed)}`;
        }
        const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})\s+(\d{1,2}:\d{2})/);
        if (slashMatch) {
          const [, d, m, y, t] = slashMatch;
          return `${pad(d)}/${pad(m)}/${y.slice(-2)} ${t}`;
        }
      }

      // 3. Extract HH:mm from timeVal
      let timePart = '';
      const timeMatch = str.match(/(\d{1,2}:\d{2})/);
      if (timeMatch) {
        const [h, m] = timeMatch[1].split(':');
        timePart = `${pad(h)}:${pad(m)}`;
      }

      // 4. Extract date part from order fields or selectedDate
      const dateVal = order.createDate || order.create_date || order.invoiceDate || this.selectedDate;
      let datePart = '';
      if (dateVal) {
        if (typeof dateVal === 'object') {
          const sec = dateVal._seconds || dateVal.seconds;
          if (sec) {
            datePart = toDDMMYY(new Date(sec * 1000));
          } else if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
            datePart = toDDMMYY(dateVal);
          }
        } else if (typeof dateVal === 'string') {
          const trimmed = dateVal.trim();
          if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
            const [y, m, d] = trimmed.split('T')[0].split(' ')[0].split('-');
            datePart = `${pad(d)}/${pad(m)}/${y.slice(-2)}`;
          } else {
            const parsed = new Date(trimmed);
            if (!isNaN(parsed.getTime())) {
              datePart = toDDMMYY(parsed);
            }
          }
        }
      }

      if (!datePart && this.selectedDate) {
        const d = new Date(this.selectedDate);
        if (!isNaN(d.getTime())) {
          datePart = toDDMMYY(d);
        }
      }

      if (datePart && timePart) {
        return `${datePart} ${timePart}`;
      }
      if (datePart && !timePart && str) {
        return `${datePart} ${str}`;
      }
      if (timePart) {
        return timePart;
      }
      return str || '-';
    },

    formatInvoiceDate(order) {
      if (!order) return '-';
      const val = order.invoiceDate || order.invoice_date || order.invoiceDate_sl || order.invoice_date_sl;
      if (!val) return '-';

      const pad = (n) => String(n).padStart(2, '0');
      const formatD = (d) => {
        const day = pad(d.getDate());
        const month = pad(d.getMonth() + 1);
        const year = String(d.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
      };

      if (typeof val === 'object') {
        const sec = val._seconds || val.seconds;
        if (sec) {
          return formatD(new Date(sec * 1000));
        }
        if (val instanceof Date && !isNaN(val.getTime())) {
          return formatD(val);
        }
      }

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
          const [y, m, d] = trimmed.split('T')[0].split(' ')[0].split('-');
          return `${d}/${m}/${y.slice(-2)}`;
        }
        if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
          const [d, m, y] = trimmed.split('/');
          return `${pad(d)}/${pad(m)}/${y.slice(-2)}`;
        }
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
          return formatD(parsed);
        }
        return trimmed;
      }

      try {
        const parsed = new Date(val);
        if (!isNaN(parsed.getTime())) {
          return formatD(parsed);
        }
      } catch (e) {
        // ignore
      }

      return String(val);
    },

    formatAmount(val) {
      if (val === undefined || val === null || val === '') return '0.00';
      const num = Number(val);
      return isNaN(num) ? '0.00' : num.toFixed(2);
    },

    getRupees(amt) {
      let val = Number(amt || 0);
      let parts = val.toFixed(2).split('.');
      return parts[0];
    },

    getPaise(amt) {
      let val = Number(amt || 0);
      let parts = val.toFixed(2).split('.');
      return parts[1] || '00';
    },

    openReceiptPreview(order) {
      this.selectedOrderForReceipt = order;
      this.showReceiptPreview = true;
    },

    closeReceiptPreview() {
      this.showReceiptPreview = false;
      this.selectedOrderForReceipt = null;
    },

    async printPOSReceipt() {
      if (this.$htmlToPaper) {
        await this.$htmlToPaper('pos-receipt');
      } else {
        window.print();
      }
    },

    async printESCPOSDirectOrder(type = 'auto') {
      if (!this.selectedOrderForReceipt || this.printInProgress) return;

      this.printInProgress = true;
      this.addMessage({ messageClass: "info", message: "Sending ESC/POS receipt directly to USB printer..." });

      try {
        const options = { orgName: this.orgName || 'Ketco' };
        if (type && type !== 'auto') {
          options.connectionType = type;
        }
        await printESCPOSDirect(this.selectedOrderForReceipt, options);
        this.addMessage({ messageClass: "success", message: "ESC/POS receipt printed successfully." });
      } catch (err) {
        console.warn('Printer error:', err);
        this.addMessage({ messageClass: "danger", message: err.message || "Failed to print ESC/POS receipt." });
      } finally {
        this.printInProgress = false;
      }
    },

    openInvoicePreview(order) {
      this.selectedOrderForInvoice = order;
      this.showInvoicePreview = true;
    },

    closeInvoicePreview() {
      this.showInvoicePreview = false;
      this.selectedOrderForInvoice = null;
    },

    async printTaxInvoice() {
      if (this.$htmlToPaper) {
        await this.$htmlToPaper('tax-invoice');
      } else {
        window.print();
      }
    },

    getItemDisplayName(item) {
      if (!item) return '';
      let item1 = item.item1Name || '';
      let item2 = item.name || item.title || '';
      if (item1 && item2 && item1 !== item2) {
        return item1 + ' ' + item2;
      }
      return item2 || item1 || 'Item';
    },

    getItemLineTotal(item) {
      if (!item) return 0;
      let price = Number(item.price || 0);
      let qty = Number(item.qty || item.quantity || 1);
      return price * qty;
    },

    getItemSubtotal(item) {
      if (!item) return '0.00';
      let price = Number(item.price || 0);
      let qty = Number(item.qty || item.quantity || 1);
      if (item.total !== undefined) {
        return Number(item.total).toFixed(2);
      }
      return (price * qty).toFixed(2);
    }
  }
};
</script>

<style scoped>
.table-responsive {
  max-height: 600px;
}

.receipt-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.65);
  z-index: 2000;
}

.pos-receipt-container {
  width: 280px;
  font-family: 'Courier New', Courier, monospace;
  color: #000;
  box-shadow: 0 0 5px rgba(0,0,0,0.1);
  line-height: 1.3;
}

.tax-invoice-container {
  width: 210mm;
  max-width: 100%;
  font-family: 'Times New Roman', Times, serif;
  color: #000;
  line-height: 1.4;
  background-color: #fff;
  border: 1px solid #000 !important;
  box-sizing: border-box;
}

.invoice-table th, .invoice-table td {
  border-color: #000 !important;
}

@media print {
  @page {
    size: A4 portrait;
    margin: 10mm 12mm;
  }
  body * {
    visibility: hidden !important;
  }
  #pos-receipt, #pos-receipt * {
    visibility: visible !important;
  }
  #tax-invoice, #tax-invoice * {
    visibility: visible !important;
  }
  #pos-receipt {
    position: absolute;
    left: 0;
    top: 0;
    width: 80mm;
    margin: 0;
    padding: 10px !important;
    box-shadow: none !important;
  }
  #tax-invoice {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    max-width: 210mm;
    margin: 0;
    padding: 15px !important;
    box-shadow: none !important;
    border: 1px solid #000 !important;
  }
}
</style>
